import React, { useMemo, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "../../DataContext"; // Import the context
import SummaryPage from "../summary-page/summary-page.js"; // Import your summary template component
import GenericTrackingComponent from "../common/generic-tracking-page/generic-tracking-page.js";
import AWBTrackingPage from "../common/awb-tracking-page/awb-tracking-page.js"; // Ensure you import the updated AWBListV2 component
import FooterContent from "../common/footer-content/footer-content.js"; // Ensure you import FooterContent
import HeaderContent from "../common/header-content/header-content.js";

const formatDate = (date) => {
    if (date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const day = String(date.getDate()).padStart(2, '0');
        return `${day}/${month}/${year}`;
    }
    return "";
};

const parseDate = (dateString) => {
  if (!dateString) return null;
  const [day, month, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day); // month is 0-based
};

const isValidSrefOrAwb = (value) => { 
  // Regex to check for <script> tags (case insensitive)
  const scriptTagRegex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/i; 
  return !scriptTagRegex.test(value); 
};

const isValidDate = (dateString) => {
  const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/; // Validates dd/mm/yyyy format
  return regex.test(dateString);
};

const DynamicMRTComponent = () => {
    const location = useLocation();
    const source = location.state?.source;
    const navigate = useNavigate();
    const { trackingParameters, clients, loading } = useData(); // Get the tracking parameters and clients from context
    const [error, setError] = React.useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [pageName, setPageName] = useState(null);

  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 14);
  const ninetyDaysAgo = new Date();
  ninetyDaysAgo.setDate(today.getDate() - 90);

      // Function to parse query parameters from the URL
  const queryParams = new URLSearchParams(location.search);
  const lowerCaseParams = new URLSearchParams();

  for (const [key, value] of queryParams.entries()) {
    if (key.toLowerCase() === 'custcode' || key.toLowerCase() === 'locale') {
      lowerCaseParams.set(key.toLowerCase(), value.toLowerCase());
    } else {
      lowerCaseParams.set(key.toLowerCase(), value); // Keep the original value
    }
  }

  const parametersFromUrl = {
    custcode: lowerCaseParams.get("custcode"),
    sref: lowerCaseParams.get("sref"),
    awb: lowerCaseParams.get("awb"),
    locale: lowerCaseParams.get("locale"),
    fromDate: lowerCaseParams.get("from_date"),
    toDate: lowerCaseParams.get("to_date"),
    origin: lowerCaseParams.get("origin"),
    destination: lowerCaseParams.get("destination")
  };
    const isDynamic = !trackingParameters || Object.keys(trackingParameters).length === 0;

    // Determine which parameters to use
    const parameters = parametersFromUrl.custcode ? parametersFromUrl : trackingParameters;
    const custCode = parametersFromUrl.custcode || trackingParameters.custcode;
 
    useEffect(() => {
      // Initialize variables for validation
      if(parameters.custcode){
        const srefValues = parameters.sref ? parameters.sref.split(",").map(sref => sref.trim()) : [];
        const awbValues = parameters.awb ? parameters.awb.split(",").map(awb => awb.trim()) : [];
        const newFromDate = parameters.fromDate;
        const newToDate = parameters.toDate;
    
        // Validate sref values
        if (srefValues.length > 10) {
            setError("Please enter a maximum of 10 Shipper References.");
            setPageName(custCode);
            return;
        }
        const invalidSref = srefValues.find(sref => !isValidSrefOrAwb(sref));
        if (invalidSref) {
            //setError("Shipper reference cannot contain special character, Only + and - symbol are allowed in the shipper reference field.");
            setError("Script tags are not allowed.");
            setPageName(custCode);
            return;
        }
    
        // Validate awb values
        if (awbValues.length > 10) {
            setError("Please enter a maximum of 10 AWBs.");
            setPageName(custCode);
            return;
        }
        const invalidAwb = awbValues.find(awb => !isValidSrefOrAwb(awb));
        if (invalidAwb) {
            //setError("Awb number cannot contain special character, Only + and - symbol are allowed in the shipper reference field.");
            setError("Script tags are not allowed.");
            setPageName(custCode);
            return;
        }
    
        // Validate dates
        if (newFromDate && !isValidDate(newFromDate)) {
            setError("From date must be in the format dd/mm/yyyy.");
            setPageName(custCode);
            return;
        }
        if (newToDate && !isValidDate(newToDate)) {
            setError("To date must be in the format dd/mm/yyyy.");
            setPageName(custCode);
            return;
        }
    
        // Parse dates for further validation
        const parsedFromDate = parseDate(newFromDate);
        const parsedToDate = parseDate(newToDate);
    
        // Additional date checks
        const formattedNinetyDaysAgo = new Date(ninetyDaysAgo.setHours(0, 0, 0, 0));
        const formattedToday = new Date(today.setHours(0, 0, 0, 0));
        let maxValidToDate;
    
        if (parsedFromDate) {
            const AfterNinetyDays = new Date(parsedFromDate);
            AfterNinetyDays.setDate(parsedFromDate.getDate() + 90);
            maxValidToDate = AfterNinetyDays > formattedToday ? formattedToday : AfterNinetyDays;
        }
    
        if (
            (parsedFromDate && (parsedFromDate < formattedNinetyDaysAgo || parsedFromDate >= formattedToday)) ||
            (parsedToDate && (parsedToDate < parsedFromDate || parsedToDate > maxValidToDate))
        ) {
            setPageName(custCode);
            if (parsedFromDate > parsedToDate) {
                setError("From date should be before the to date.");
            } else {
                setError("Unfortunately, the date range must be within the last 90 days.");
            }
        } else {
            setError(null);
            setFromDate(newFromDate || formatDate(startDate));
            setToDate(newToDate || formatDate(today));
        }
      }
  }, [trackingParameters, parametersFromUrl, startDate, today, custCode]);

      // Ensure paramsWithDefaultDates always has valid values
  const paramsWithDefaultDates = useMemo(() => ({
    ...parameters,
    fromDate: fromDate ?? trackingParameters.fromDate ?? formatDate(startDate),
    toDate: toDate ?? trackingParameters.toDate ?? formatDate(today),
    sref: parameters.sref 
    ? [...new Set(parameters.sref.split(',').map(sref => sref.trim()))].join(',') 
    : parameters.sref,
}), [parameters, fromDate, toDate, trackingParameters, startDate, today]);

  // Use useMemo to filter clients based on the customer code
  const client = useMemo(() => {
    if (!clients || !Array.isArray(clients.clientConfig)) {
      return null; // Return null if clientConfig is not available
    }

    const matchedClients = clients.clientConfig.filter((c) =>
        c.customerCode && custCode && c.customerCode.toLowerCase() === custCode.toLowerCase()
    );

    return matchedClients.length > 0 ? matchedClients[0] : null; // Return the first matched client or null
  }, [clients.clientConfig, custCode]);

  const srefTrackingPage = parameters.sref;
  const awbTrackingPage = !parameters.sref && parameters.awb;
  const hasNavigated = useRef(false);

  useEffect(() => {
    const srefOrAwb = parameters.sref || parameters.awb;

    if (!loading) {
      if (!custCode) {
        if (window.location.search === "") { // Check if there are no other parameters
          navigate("/");
        } else {
          // Show error message for invalid customer code
          setPageName("");
          setError("Customer code cannot be invalid or blank.");
        }
      } else if (!client) {
        // If custCode is not blank but client is blank
        setError("Customer code cannot be invalid.");
        setPageName("");
      } else if (custCode && !srefOrAwb) {
        // If custCode is not blank but does not contain sref/awb
        navigate(`/${custCode}`);
      } else if (!loading && !hasNavigated.current) {
        hasNavigated.current = true; // Set the flag to true
        // Additional logic can be added here if necessary
      } else {
        hasNavigated.current = false; // Reset the flag if conditions are not met
      }
    }
  }, [client, parameters, navigate, loading]);

  if (loading) {
    return <p></p>;
  }

  if (error) {
    return <div className="outer-body-container text-02">
        <HeaderContent toDisplayClientLogo={false}  locale="en" countryCode="gb" />

        <div className="body-content">
          {/* <div className="column-left"></div> */}
          <div className="box column-middle">
            <div className="contentbody">
              <p className="color-red sorry-text">
                Sorry!
              </p>
              <div style={{ borderBottom: '2px solid black', margin: '10px 0', marginRight: '90%' }} />
              <p className="top-gap" style={{fontSize: '20px', fontFamily: '300'}}>
              {error}{" "}Please check the URL or return to the{" "} <a href={`/${pageName}`}>home page</a>
              .</p>
            </div>
          </div>
          {/* <div className="column-left"></div> */}
        </div>

        <FooterContent />
      </div>
  }

 // Determine whether to display the summary or the generic tracking component
 const shouldDisplaySummary = client && client.isSummaryPage?.toUpperCase() === "Y";
 
 // Split AWB numbers into an array if present
 const awbNumbers = parameters.awb 
 ? [...new Set(parameters.awb.split(',').map(awb => awb.trim()))] 
 : [];

 // Render the appropriate component based on the client data
 if (shouldDisplaySummary && !error) {
     return <SummaryPage parameters={paramsWithDefaultDates} client={client} />;
 } else if (srefTrackingPage && !error) {
     return <GenericTrackingComponent parameters={paramsWithDefaultDates} client={client} source={source} isDynamic={isDynamic}/>;
 } else if (awbTrackingPage && !error) {
     return <AWBTrackingPage awbNumbers={awbNumbers} locale={paramsWithDefaultDates.locale} client={client} isDynamic={isDynamic} source={source} custcode={paramsWithDefaultDates.custcode}/>;
 }

 return null; // Fallback if no conditions are met
};

export default DynamicMRTComponent;